import React, { createContext, useEffect, useCallback, useContext, useReducer, useRef } from 'react';
import { AUTHENTICATION_TOKEN } from '../../constants/common';
import { START_CHAT_API } from '../../utils/apiEndpoints';
import aiApiClient from '../../apiClient/ai-api-client';
import { getToken } from '../../utils/cookie';
import { useNavigate } from 'react-router-dom';
import { message as antdmessage } from 'antd';
import config from './../../../config.json';

const AIChatContext = createContext();

const initialState = {
  conversations: {}, // List of all conversations
  currentConversation: null,
  isAnswering: false,
  context: { label: 'All', value: 'all', isWorkspace: false, isFile: false },
  error: null,
  loading: false,
  conversationsLoading: false,
  attachedFiles: [],
  processingSteps: [],
  referenceSources: [],
};

const ACTIONS = {
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_CURRENT_CONVERSATION: 'SET_CURRENT_CONVERSATION',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_ANSWER: 'UPDATE_ANSWER',
  SET_CONTEXT: 'SET_CONTEXT',
  SET_ERROR: 'SET_ERROR',
  SET_LOADING: 'SET_LOADING',
  SET_CONVERSATIONS_LOADING: 'SET_CONVERSATIONS_LOADING',
  REMOVE_LAST_MESSAGE: 'REMOVE_LAST_MESSAGE',
  UPDATE_CONVERSATION: 'UPDATE_CONVERSATION',
  DELETE_CONVERSATION: 'DELETE_CONVERSATION',
  SET_ATTACHED_FILES: 'SET_ATTACHED_FILES',
  SET_PROCESSING_STEPS: 'SET_PROCESSING_STEPS',
  SET_REFERENCE_SOURCES: 'SET_REFERENCE_SOURCES',
  RESET: 'RESET',
};
const chatReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };
    case ACTIONS.SET_CURRENT_CONVERSATION:
      return { ...state, currentConversation: action.payload };
    case ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          messages: [...(state.currentConversation?.messages || []), action.payload],
        },
      };
    case ACTIONS.UPDATE_ANSWER:
      const messages = state.currentConversation?.messages || [];
      const lastIndex = messages.length - 1;
      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          messages: messages.map((msg, index) => (index === lastIndex ? { ...msg, ...action.payload } : msg)),
        },
      };
    case ACTIONS.SET_CONTEXT:
      return { ...state, context: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_CONVERSATIONS_LOADING:
      return { ...state, conversationsLoading: action.payload };
    case ACTIONS.REMOVE_LAST_MESSAGE:
      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          messages: state.currentConversation.messages.slice(0, -1),
        },
      };
    case ACTIONS.UPDATE_CONVERSATION:
      return {
        ...state,
        conversations: Object.fromEntries(
          Object.entries(state.conversations).map(([key, convArray]) => [
            key,
            convArray.map((conv) => (conv.id === action.payload.id ? { ...conv, ...action.payload } : conv)),
          ])
        ),
      };
    case ACTIONS.DELETE_CONVERSATION:
      return {
        ...state,
        conversations: Object.fromEntries(
          Object.entries(state.conversations).map(([key, convArray]) => [
            key,
            convArray.filter((conv) => conv.id !== action.payload),
          ])
        ),
        currentConversation: state.currentConversation?.id === action.payload ? null : state.currentConversation,
      };
    case ACTIONS.SET_ATTACHED_FILES:
      return { ...state, attachedFiles: action.payload };
    case ACTIONS.SET_PROCESSING_STEPS:
      return { ...state, processingSteps: action.payload };
    case ACTIONS.SET_REFERENCE_SOURCES:
      return { ...state, referenceSources: action.payload };
    case ACTIONS.RESET:
      return initialState;
    default:
      return state;
  }
};

export const AiChatProvider = ({ children }) => {
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Helper function to add files while checking for duplicates
  const addFilesWithDuplicateCheck = useCallback(
    (newFiles) => {
      if (!Array.isArray(newFiles) || newFiles.length === 0) return;

      const existingFileIds = new Set(state.attachedFiles.map((file) => file.id));
      const uniqueNewFiles = newFiles.filter((file) => !existingFileIds.has(file.id));

      if (uniqueNewFiles.length > 0) {
        dispatch({ type: ACTIONS.SET_ATTACHED_FILES, payload: [...state.attachedFiles, ...uniqueNewFiles] });
      }
    },
    [state.attachedFiles, dispatch]
  );

  const generateConversationId = useCallback(
    async (title, attachedFiles, meta_info = {}) => {
      try {
        const files = attachedFiles
          .filter((file) => !file.isWorkspace && file.type !== 'template')
          .map((file) => file.id);
        const workspaces = attachedFiles.filter((file) => file.isWorkspace).map((file) => file.id);
        const updatedMetaInfo = {
          ...meta_info,
          context: {
            // ...meta_info.context,
            files,
            workspaces,
            action: 'add',
          },
        };
        const response = await aiApiClient.post('/conversation', { title, meta_info: updatedMetaInfo });
        addFilesWithDuplicateCheck(attachedFiles);
        return response.id;
      } catch (error) {
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'failed to generated conversation ID.' });
        antdmessage.error('Failed to generate conversation ID.');
        return null;
      }
    },
    [addFilesWithDuplicateCheck]
  );

  const updateConversationById = useCallback(
    async (conversationId, attachedFiles) => {
      try {
        const files = attachedFiles
          .filter((file) => !file.isWorkspace && file.type !== 'template')
          .map((file) => file.id);
        const workspaces = attachedFiles.filter((file) => file.isWorkspace).map((file) => file.id);
        const meta_info = {
          context: {
            files,
            workspaces,
            action: 'add',
          },
        };

        await aiApiClient.put(`/conversation/${conversationId}`, { meta_info });
        addFilesWithDuplicateCheck(attachedFiles);
      } catch (error) {
        console.error('Failed to update conversation:', error);
        antdmessage.error('Failed to update conversation with attached files.');
      }
    },
    [addFilesWithDuplicateCheck]
  );

  const streamAnswer = useCallback(
    async ({ query, value, isWorkspace = false, isFile = false, conversationId }) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const user = JSON.parse(localStorage.getItem('persist:user'));
      const organization = user?.userOrganizationId?.slice(1, -1);
      const authToken = getToken(AUTHENTICATION_TOKEN);
      const messages = state.currentConversation?.messages || [];
      const currentMessage = messages[messages.length - 1];
      let currentContent =
        currentMessage?.from === 'ai' &&
        (currentMessage?.status === 'pending' || currentMessage?.status === 'answering')
          ? currentMessage?.content
          : '';

      try {
        const isNotAllContext = isWorkspace !== false || isFile !== false;
        const id = isNotAllContext ? value : null;
        const rag_filters = {
          [!isWorkspace ? 'source_id' : 'workspace_id']: id,
        };

        // Make the fetch request for streaming data
        const response = await fetch(`${config.ONEDOC_AI_URL}/v2/ai/chat/stream`, {
          method: 'POST',
          headers: { Authorization: authToken, 'X-Organization': organization, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: query,
            conversation: conversationId,
            rag_filters,
          }),
          signal: abortControllerRef.current.signal,
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let buffer = '';
        while (!done) {
          const { done: isDone, value } = await reader.read();
          done = isDone;
          const decodedText = decoder.decode(value, { stream: true });
          buffer += decodedText;

          let lines = buffer.split('\n');
          buffer = lines.pop(); // Retain any incomplete JSON in the buffer for next read

          for (const line of lines) {
            if (line.startsWith('data: event:')) {
              try {
                const eventArray = JSON.parse(line.slice(12)); // Remove 'data: event:'

                // Process each event type in the array
                for (const eventData of eventArray) {
                  if (eventData.type === 'STEPS_INFO') {
                    // Only dispatch if content exists and is different from current state
                    if (eventData.content && eventData.content.length > 0) {
                      const currentSteps = state.processingSteps || [];
                      const newSteps = eventData.content;

                      // Deep comparison of steps to prevent unnecessary updates
                      const hasChanges =
                        newSteps.length !== currentSteps.length ||
                        newSteps.some((step, index) => {
                          const currentStep = currentSteps[index];
                          return (
                            !currentStep ||
                            step.id !== currentStep.id ||
                            step.text !== currentStep.text ||
                            step.status !== currentStep.status
                          );
                        });

                      if (hasChanges) {
                        dispatch({
                          type: ACTIONS.SET_PROCESSING_STEPS,
                          payload: newSteps,
                        });
                      }
                    }
                  } else if (eventData.type === 'ANSWER') {
                    if (eventData.content && eventData.content.length > 0) {
                      const newContent = eventData.content.join('');
                      // Only update if content has changed
                      if (newContent !== currentContent) {
                        currentContent = currentContent + newContent;

                        dispatch({
                          type: ACTIONS.UPDATE_ANSWER,
                          payload: {
                            ...currentMessage,
                            content: currentContent,
                            status: 'answering',
                            timestamp: Date.now(),
                            // id: eventData.id,
                            // parent_id: eventData.parent_id,
                          },
                        });
                      }
                    }
                  }
                }
              } catch (e) {
                console.error('Failed to parse event data:', e);
                throw e;
              }
            } else if (line.startsWith('data: end:')) {
              try {
                const endData = JSON.parse(line.slice(10)); // Remove 'data: end:'

                // Mark the conversation as completed
                dispatch({
                  type: ACTIONS.UPDATE_ANSWER,
                  payload: {
                    content: currentContent,
                    status: 'answered',
                    timestamp: Date.now(),
                    finishReason: endData.finishReason,
                    usage: endData.usage,
                    id: endData?.meta_info?.current_msg_id,
                    parent_id: endData?.meta_info?.parent_id,
                  },
                });
                done = true; // End the streaming loop
                break;
              } catch (e) {
                console.error('Failed to parse end data:', e);
                // Still end the stream even if parsing fails
                dispatch({
                  type: ACTIONS.UPDATE_ANSWER,
                  payload: {
                    content: currentContent,
                    status: 'answered',
                    timestamp: Date.now(),
                  },
                });
                done = true;
                break;
              }
            }
          }

          if (done) {
            break;
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          dispatch({
            type: ACTIONS.UPDATE_ANSWER,
            payload: {
              content: currentContent.length === 0 ? 'Error occurred while generating response.' : currentContent,
              status: 'aborted_error',
              timestamp: Date.now(),
            },
          });

          return;
        }
        console.error('Stream error:', error);
        setError(error.message);
        throw error;
      }
    },
    [aiApiClient, state.currentConversation]
  );

  const fetchAnswer = useCallback(async ({ query, value, isWorkspace, isFile, url, conversationId }) => {
    const id = value;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    try {
      const isNotAllContext = isWorkspace !== false || isFile !== false;
      const response = await aiApiClient.get(
        url || START_CHAT_API({ query, id: isNotAllContext ? id : null, isWorkspace, conversationId }),
        { signal: abortControllerRef.current.signal }
      );
      return response;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    }
  }, []);

  const deleteConversationById = useCallback(
    async (id) => {
      try {
        await aiApiClient.delete(`/conversation/${id}`);
        dispatch({ type: ACTIONS.DELETE_CONVERSATION, payload: id });
        if (id == state.currentConversation?.id) navigate('/chat');
      } catch (error) {
        antdmessage.error('Failed to delete conversation.');
      }
    },
    [state.currentConversation]
  );

  const sendFeedback = useCallback(async ({ conversationId, parentId, liked, reason }) => {
    try {
      const response = await aiApiClient.post(`/conversation/feedback`, {
        conversation_id: conversationId,
        parent_id: parentId,
        liked,
        reason,
      });
      return response;
    } catch (error) {
      console.error('Failed to send feedback:', error);
      antdmessage.error('Failed to submit feedback. Please try again.');
    }
  });

  const sendMessage = useCallback(
    async (message, { shouldNavigate = false, enableChatStream = true }) => {
      if (state.isAnswering) return;

      if (message?.allowGoBack) {
        dispatch({
          type: ACTIONS.ADD_MESSAGE,
          payload: { from: 'user', content: message.content, timestamp: Date.now(), allowGoBack: true },
        });
        return;
      }

      try {
        let conversationId = state.currentConversation?.id;

        // Create context file object if context exists and is not 'all'
        const contextFile =
          state.context?.value && state.context?.value !== 'all'
            ? {
                type: state.context.isFile ? 'file' : 'workspace',
                id: state.context.value,
                title: state.context.label,
                isDirectlyAttached: true,
                isWorkspace: state.context.isWorkspace || false,
              }
            : null;

        // Merge context with user attached files for the message
        let messageAttachedFiles = message.attachedFiles || [];
        if (contextFile) {
          // Check if contextFile already exists in attached files
          const alreadyExistsInMessage = messageAttachedFiles.some(
            (file) => file.id === contextFile.id && file.type === contextFile.type
          );
          const alreadyExistsInState = state.attachedFiles?.some(
            (file) => file.id === contextFile.id && file.type === contextFile.type
          );
          if (!alreadyExistsInMessage && !alreadyExistsInState) {
            messageAttachedFiles = [...messageAttachedFiles, contextFile];
          }
        }

        if (!conversationId) {
          conversationId = await generateConversationId(message.question, messageAttachedFiles, {
            context: state.context,
          });

          const newConversation = { id: conversationId, title: message.question };

          const updatedConversations = {
            ...state.conversations,
            Today: state.conversations.Today ? [newConversation, ...state.conversations.Today] : [newConversation],
          };

          dispatch({
            type: ACTIONS.SET_CONVERSATIONS,
            payload: updatedConversations,
          });

          dispatch({
            type: ACTIONS.SET_CURRENT_CONVERSATION,
            payload: { id: conversationId, messages: [...(state.currentConversation?.messages || [])] },
          });

          if (shouldNavigate) navigate(`/chat/${conversationId}`, { state: { isNewChat: true } });
        } else if (messageAttachedFiles.length > 0) {
          // If there are attached files in an existing conversation, update it
          await updateConversationById(conversationId, messageAttachedFiles);
        }

        dispatch({
          type: ACTIONS.ADD_MESSAGE,
          payload: {
            from: 'user',
            content: message.question,
            timestamp: Date.now(),
            attachedFiles: messageAttachedFiles,
          },
        });

        if (message.isAnswerable) {
          clearProcessingData();
          // Add pending AI message
          dispatch({
            type: ACTIONS.ADD_MESSAGE,
            payload: { from: 'ai', content: '', status: 'pending', timestamp: Date.now() },
          });

          // Stream AI response
          if (enableChatStream) {
            return await streamAnswer({
              query: message.question,
              ...(state.context || {}),
              url: message.url,
              conversationId,
            });
          }

          // Fetch AI response
          const response = await fetchAnswer({
            query: message.question,
            ...(state.context || {}),
            url: message.url,
            conversationId,
          });
          if (response?.error) {
            dispatch({
              type: ACTIONS.UPDATE_ANSWER,
              payload: {
                content:
                  '**No Response**\nUnfortunately, there is no content available at the moment. Please try again later.',
                status: 'answered',
                from: 'ai',
              },
            });
            antdmessage.error('Content not available at the moment. Please try again.');
          } else {
            // Update AI message with response
            dispatch({
              type: ACTIONS.UPDATE_ANSWER,
              payload: {
                content: response?.response?.content || response?.answer,
                sources: response?.context,
                status: 'answered',
                from: 'ai',
              },
            });
          }
        }
      } catch (error) {
        dispatch({
          type: ACTIONS.UPDATE_ANSWER,
          payload: {
            content: error?.detail?.message || error?.response || 'Internal server error',
            status: 'error',
          },
        });
      }
    },
    [
      state.currentConversation,
      state.conversations,
      state.context,
      state.isAnswering,
      state.attachedFiles,
      generateConversationId,
      updateConversationById,
      fetchAnswer,
      navigate,
      dispatch,
    ]
  );

  const fetchAllConversations = useCallback(async (query) => {
    dispatch({ type: ACTIONS.SET_CONVERSATIONS_LOADING, payload: true });
    try {
      const params = { limit: 100, group_by_date: true };
      if (query.trim() !== '') params['q'] = query;
      const response = await aiApiClient.get('/conversation', { params: params });
      dispatch({ type: ACTIONS.SET_CONVERSATIONS, payload: response || [] });
    } catch (error) {
      antdmessage.error('Failed to fetch conversations');
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch conversations' });
    } finally {
      dispatch({ type: ACTIONS.SET_CONVERSATIONS_LOADING, payload: false });
    }
  }, []);

  const fetchConversationById = useCallback(async (id) => {
    if (!id) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await aiApiClient.get(`/conversation/${id}/messages`);

      // Process files and workspaces from the response
      const processedFiles = [
        ...(response.file || []).map((file) => ({
          ...file,
          type: 'file',
          isDirectlyAttached: false,
          isWorkspace: false,
        })),
        ...(response.workspace || []).map((workspace) => ({
          ...workspace,
          type: 'workspace',
          isDirectlyAttached: false,
          isWorkspace: true,
        })),
      ];

      // Dispatch the processed files
      dispatch({ type: ACTIONS.SET_ATTACHED_FILES, payload: processedFiles });

      dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: response });
      dispatch({ type: ACTIONS.UPDATE_CONVERSATION, payload: response });

      return response;
    } catch (error) {
      antdmessage.error('Failed to fetch conversation');
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch conversation' });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  const setContext = useCallback((newContext) => {
    dispatch({ type: ACTIONS.SET_CONTEXT, payload: newContext });
  }, []);

  const createNewConversation = useCallback(() => {
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: null });
    // Clear processing steps and sources for new conversation
    dispatch({ type: ACTIONS.SET_PROCESSING_STEPS, payload: [] });
    dispatch({ type: ACTIONS.SET_REFERENCE_SOURCES, payload: [] });
  }, []);

  const removeLastMessage = useCallback(() => {
    const messages = state.currentConversation?.messages || [];
    const lastMessage = messages[messages.length - 1];

    if (lastMessage?.allowGoBack) {
      dispatch({ type: ACTIONS.REMOVE_LAST_MESSAGE });
    }
  }, [state.currentConversation]);

  const resetCurrentConversation = useCallback(() => {
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: null });
    dispatch({ type: ACTIONS.SET_ATTACHED_FILES, payload: [] });
  }, []);

  const setAttachedFiles = useCallback(
    (files) => {
      if (Array.isArray(files)) {
        // For arrays, use the helper function to check for duplicates
        addFilesWithDuplicateCheck(files);
      } else if (files && typeof files === 'object') {
        // push single file object to the array if it doesn't already exist
        if (!state.attachedFiles.find((file) => file.id === files.id)) {
          dispatch({ type: ACTIONS.SET_ATTACHED_FILES, payload: [...state.attachedFiles, files] });
        }
      } else {
        dispatch({ type: ACTIONS.SET_ATTACHED_FILES, payload: [] });
      }
    },
    [state.attachedFiles, dispatch, addFilesWithDuplicateCheck]
  );

  const clearProcessingData = useCallback(() => {
    dispatch({ type: ACTIONS.SET_PROCESSING_STEPS, payload: [] });
    dispatch({ type: ACTIONS.SET_REFERENCE_SOURCES, payload: [] });
  }, []);

  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isAnswering =
    state.loading ||
    (state.currentConversation?.messages?.length > 0 &&
      (state.currentConversation.messages[state.currentConversation.messages.length - 1]?.status === 'pending' ||
        state.currentConversation.messages[state.currentConversation.messages.length - 1]?.status === 'answering'));

  const value = {
    conversations: state.conversations,
    currentConversation: state.currentConversation,
    context: state.context,
    error: state.error,
    loading: state.loading,
    isAnswering,
    sendMessage,
    setContext,
    removeLastMessage,
    fetchConversationById,
    fetchAllConversations,
    createNewConversation,
    deleteConversationById,
    resetCurrentConversation,
    sendFeedback,
    attachedFiles: state.attachedFiles,
    setAttachedFiles,
    processingSteps: state.processingSteps,
    referenceSources: state.referenceSources,
    clearProcessingData,
    handleAbort,
  };

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
};

export const useAIChat = () => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChat must be used within an AiChatProvider');
  }
  return context;
};

export default AiChatProvider;
